import CryptoJS from 'crypto-js';
import { store, mutations } from '../store';
import { API_URL, getChatApiUrl } from './config';
import { memoryDB } from '../utils/db';
import { buildRecallNote, getCompressedBoundary } from '../utils/sessionMemory';
import {
  SENSITIVE_CONTENT_BLOCK_MESSAGE,
  createSensitiveContentFilter
} from '../utils/sensitiveContent';

let lastPayloadMd5 = null;

const MARKDOWN_IMAGE_URL_REGEX = /!\[[^\]]*]\((https?:\/\/[^\s)]+|data:image\/[^\s)]+)\)/gi;
const SENSITIVE_MODEL_LOCK_MARKER = '[AIHELP_INTERNAL_SENSITIVE_MODEL_LOCK]';
const TIER_LOCK_MARKER = '[AIHELP_INTERNAL_TIER_LOCK]';
const IMAGE_EDIT_FOLLOW_UP_PATTERNS = [
  /(基于|根据|参考|按照|沿用|保留).{0,18}(上|前|这|该|刚|原|之前)?(张|幅|个)?(图|图片|图像|照片|海报|作品)/i,
  /(把|将).{0,12}(这张|上一张|刚才那张|这幅|原图|它).{0,20}(改|换|调|变|修|做)/i,
  /(改一下|改成|微调|调整|优化|换色|换背景|重绘|扩图|放大|裁剪|融合|合成|延展|去掉|加上|抠图|局部重绘)/i,
  /\b(edit|modify|variation|variant|iterate on|based on|using (the )?(previous|same|this) image|reference image|keep the subject)\b/i
];

function uniqueUrls(urls = []) {
  return [...new Set(urls.filter(url => typeof url === 'string' && url.trim()).map(url => url.trim()))];
}

function extractImageUrlsFromContent(content = '') {
  if (typeof content !== 'string' || !content) {
    return [];
  }

  const urls = [];
  let match;
  while ((match = MARKDOWN_IMAGE_URL_REGEX.exec(content)) !== null) {
    if (match[1]) {
      urls.push(match[1]);
    }
  }

  MARKDOWN_IMAGE_URL_REGEX.lastIndex = 0;
  return uniqueUrls(urls);
}

function extractMessageImageUrls(message = {}) {
  if (!message || typeof message !== 'object') {
    return [];
  }

  const urls = [];

  if (Array.isArray(message.generatedImageUrls)) {
    urls.push(...message.generatedImageUrls);
  }

  if (typeof message.imageUrl === 'string' && message.imageUrl) {
    urls.push(message.imageUrl);
  }

  if (Array.isArray(message.attachments)) {
    message.attachments.forEach(att => {
      if (att && att.type === 'image' && att.url) {
        urls.push(att.url);
      }
    });
  }

  urls.push(...extractImageUrlsFromContent(message.content));
  return uniqueUrls(urls);
}

function shouldUseServerExecution(text = '', extraParams = {}) {
  if (extraParams && (extraParams.confirmed_command ||
      (Array.isArray(extraParams.workspace_manifest) && extraParams.workspace_manifest.length > 0) ||
      (Array.isArray(extraParams.workspace_tool_results) && extraParams.workspace_tool_results.length > 0))) {
    return true;
  }

  const prompt = String(text || '').trim();
  if (!prompt) {
    return false;
  }

  if (extraParams && extraParams.workspace_enabled &&
      /(?:工作区|workspace|(?:分析|统计|清洗|筛选|提取|比较|合并|转换|生成|创建|制作|导出|保存).{0,20}(?:文件|文档|表格|数据|excel|word|xlsx|docx|markdown|csv|代码)|(?:文件|文档|表格|数据|excel|word|xlsx|docx|markdown|csv|代码).{0,20}(?:分析|统计|清洗|筛选|提取|比较|合并|转换|生成|创建|制作|导出|保存))/i.test(prompt)) {
    return true;
  }

  const mustExecutePattern = /(下载|抓取|爬取|批量|打包|压缩|zip|导出|上传|转换|合并(文件|图片|pdf)|运行|执行|安装|部署|命令行|shell|docker|容器|服务器|网页.*图片|把.*(文件|压缩包|zip|结果)给我|save .*file|download .*image|zip file|run script|execute command|batch process|scrape)/i;
  const codeOnlyPattern = /(写(一个)?(脚本|程序|代码)|示例代码|给我代码|怎么实现|如何实现|改代码|解释代码|优化代码|code review|review)/i;
  const directExecutionPattern = /(下载|抓取|打包|压缩|运行|执行|导出|上传|转换|部署)/i;

  if (!mustExecutePattern.test(prompt)) {
    return false;
  }

  if (codeOnlyPattern.test(prompt) && !directExecutionPattern.test(prompt)) {
    return false;
  }

  return true;
}

function findLatestConversationImageUrl(context = []) {
  for (let i = context.length - 1; i >= 0; i -= 1) {
    const urls = extractMessageImageUrls(context[i]);
    if (urls.length > 0) {
      return urls[0];
    }
  }

  return '';
}

function looksLikeImageEditFollowUp(text = '') {
  if (typeof text !== 'string') {
    return false;
  }

  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }

  return IMAGE_EDIT_FOLLOW_UP_PATTERNS.some(pattern => pattern.test(trimmed));
}

// Assuming the backend is served relative to the frontend or via proxy
// TP5 Route convention: /module/controller/action

/**
 * Sends a chat message and handles Server-Sent Events (SSE) streaming response.
 * 
 * @param {string} text - The user's message.
 * @param {Array} context - Previous chat history context.
 * @param {Object} callbacks - { onContent, onThinking, onDone, onError, onChatId, onMeta, onDecision }
 */
export async function streamChat(text, context, { onContent, onThinking, onDone, onError, onImageStart, onImageTask, onChatId, onMeta, onDecision, onKnowledgeBaseJudging, onAccountBlocked, onSensitiveContent, signal }, attachments = [], extraParams = {}) {
  // Ensure Chat ID exists (Lazy Initialization backup)
  if (!store.chatId) {
      console.warn('[streamChat] store.chatId is null, attempting to initialize...');
      if (mutations && mutations.initChatId) {
          store.chatId = mutations.initChatId();
      }
      // Fallback if still null
      if (!store.chatId) {
          const fallbackId = localStorage.getItem('aihelp_chat_id') || ('user_' + Date.now().toString(36) + Math.random().toString(36).substr(2));
          store.chatId = fallbackId;
          localStorage.setItem('aihelp_chat_id', fallbackId);
      }
      console.log('[streamChat] Chat ID initialized to:', store.chatId);
  }
  const chatId = store.chatId;

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream', // Important for SSE
  };

  // --- Typewriter Logic ---
  let typingQueue = [];
  let isTyping = false;
  let streamFinished = false;
  let isAborted = false;
  let typingTimer = null;
  let doneNotified = false;
  let abortHandler = null;
  let visibilityHandler = null;
  const contentFilter = createSensitiveContentFilter();
  let sensitiveFiltersFlushed = false;

  const cleanupStreamState = () => {
    clearTypingTimer();
    if (signal && abortHandler) {
      signal.removeEventListener('abort', abortHandler);
    }
    if (visibilityHandler && typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', visibilityHandler);
    }
  };

  const notifyDone = (allowWhenAborted = false) => {
    if (doneNotified) {
      return;
    }
    if (isAborted && !allowWhenAborted) {
      return;
    }
    doneNotified = true;
    cleanupStreamState();
    if (onDone) onDone();
  };

  const clearTypingTimer = () => {
    if (typingTimer !== null) {
      clearTimeout(typingTimer);
      typingTimer = null;
    }
  };

  const emitTypingChunk = ({ type, content }) => {
    if (type === 'think') {
      if (onThinking) onThinking(content);
    } else if (type === 'text') {
      if (onContent) onContent(content);
    }
  };

  const flushSensitiveFilters = () => {
    if (sensitiveFiltersFlushed) return;
    sensitiveFiltersFlushed = true;

    const pendingContent = contentFilter.flush();
    if (pendingContent) enqueue('text', pendingContent);
  };

  const flushTypingQueue = () => {
    if (typingQueue.length === 0) {
      isTyping = false;
      if (streamFinished) {
        notifyDone();
      }
      return;
    }

    clearTypingTimer();

    const batches = [];
    let currentBatch = null;

    while (typingQueue.length > 0) {
      const item = typingQueue.shift();
      if (!currentBatch || currentBatch.type !== item.type) {
        currentBatch = { ...item };
        batches.push(currentBatch);
      } else {
        currentBatch.content += item.content;
      }
    }

    isTyping = false;
    batches.forEach(emitTypingChunk);

    if (streamFinished) {
      notifyDone();
    }
  };

  const scheduleQueueProcessing = (delay) => {
    clearTypingTimer();
    typingTimer = setTimeout(() => {
      typingTimer = null;
      processQueue();
    }, delay);
  };

  if (signal) {
    abortHandler = () => {
      isAborted = true;
      streamFinished = true;
      typingQueue = [];
      clearTypingTimer();
      notifyDone(true);
    };
    
    if (signal.aborted) {
      abortHandler();
    } else {
      signal.addEventListener('abort', abortHandler);
    }
  }

  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        flushTypingQueue();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  const processQueue = () => {
    if (typingQueue.length === 0) {
      if (streamFinished) {
        notifyDone();
      } else {
        isTyping = false;
      }
      return;
    }

    isTyping = true;
    
    // Always process one character at a time for smooth typing
    emitTypingChunk(typingQueue.shift());

    // Dynamic delay based on queue length
    // If queue is long, speed up significantly to catch up
    // If queue is short, slow down for readability
    let delay = 30; // Base speed (ms)
    
    if (typingQueue.length > 200) delay = 1;
    else if (typingQueue.length > 100) delay = 5;
    else if (typingQueue.length > 50) delay = 10;
    else if (typingQueue.length > 20) delay = 20;
    scheduleQueueProcessing(delay);
  };

  const enqueue = (type, text) => {
    // For thinking content, process as a whole chunk to improve performance
    // This prevents UI freezing when large thinking blocks arrive
    if (type === 'think') {
        typingQueue.push({ type, content: text });
    } else {
        // Push individual characters to ensure smooth typing for regular content
        for (const char of text) {
            typingQueue.push({ type, content: char });
        }
    }
    
    if (!isTyping) {
      processQueue();
    }
  };
  // -------------------------

  // Optimize Context: Head + Tail Strategy
  // 1. Remove current prompt from history if present (to avoid duplication with backend)
  // 2. Keep System Prompt
  // 3. Check for 'summaryInfo' field in history to compress context
  const optimizeContext = (fullContext, currentPrompt, preserveCurrentPrompt = false) => {
    // Clone to avoid mutating original
    let ctx = [...fullContext];
    const hasSensitiveModelLock = ctx.some(m => m && (m.sensitiveModelLock || m.sensitive_model_lock));
    // 会话级档位锁定：取首次判定的 tier（1-5），后续轮次带回后端以跳过重复意图识别
    const lockedTierValue = (() => {
      for (const m of ctx) {
        if (m && m.tierLock != null && /^[1-5]$/.test(String(m.tierLock))) {
          return String(m.tierLock);
        }
      }
      return null;
    })();

    // Remove the last message if it matches the current prompt (deduplication)
    if (!preserveCurrentPrompt && ctx.length > 0) {
      const lastMsg = ctx[ctx.length - 1];
      if (lastMsg.role === 'user' && lastMsg.content === currentPrompt) {
        ctx.pop();
      }
    }

    let optimized = [];
    let systemPrompt = null;

    // Preserve System Prompt
    if (ctx.length > 0 && ctx[0].role === 'system') {
      systemPrompt = { role: ctx[0].role, content: ctx[0].content };
      ctx.shift();
    }

    // Helper to format message content
    const formatMsg = (m) => {
        let content = m.content;
        if (m.attachments && m.attachments.length > 0) {
            m.attachments.forEach(att => {
                if (att.type === 'file') {
                    // 对于历史记录中的超大文件，我们只保留提示，不拼接原文，避免污染上下文
                    if (att.content && att.content.length > 20000) {
                        content += `\n\n[Reference Document Content (${att.name})]:\n(Content is too large, refer to user's initial prompt or attachments)`;
                    } else {
                        content += `\n\n[Reference Document Content (${att.name})]:\n${att.content}`;
                    }
                    if (att.url) {
                        content += `\n\n[Download ${att.name}]: ${att.url}`;
                    }
                }
            });
        }
        const formattedMsg = { role: m.role, content };
        if (m.sensitiveModelLock || m.sensitive_model_lock) {
          formattedMsg.sensitive_model_lock = true;
        }
        if (m.tierLock != null && /^[1-5]$/.test(String(m.tierLock))) {
          formattedMsg.tier_lock = String(m.tierLock);
        }
        const imageAttachments = Array.isArray(m.attachments)
          ? m.attachments
              .filter(att => att && att.type === 'image' && att.url)
              .map(att => ({
                type: 'image',
                url: att.url,
                name: att.name || ''
              }))
          : [];

        if (imageAttachments.length > 0) {
          formattedMsg.attachments = imageAttachments;
        }
        if (m.imageUrl) {
          formattedMsg.imageUrl = m.imageUrl;
        }
        if (Array.isArray(m.generatedImageUrls) && m.generatedImageUrls.length > 0) {
          formattedMsg.generatedImageUrls = uniqueUrls(m.generatedImageUrls);
        }

        return formattedMsg;
    };

    // --- 寻找最近一个含有 summaryInfo 的记录 ---
    let lastSummaryIndex = -1;
    for (let i = ctx.length - 1; i >= 0; i--) {
      if (ctx[i].summaryInfo) {
        lastSummaryIndex = i;
        break;
      }
    }

    if (lastSummaryIndex !== -1) {
      // 找到了总结信息，直接丢弃在此之前（包含此条）的所有原始对话的 content
      // 提取它的 summaryInfo 作为前情提要
      const summaryText = ctx[lastSummaryIndex].summaryInfo;
      
      if (systemPrompt) optimized.push(systemPrompt);
      if (hasSensitiveModelLock) {
        optimized.push({
          role: 'system',
          content: `${SENSITIVE_MODEL_LOCK_MARKER}\nSensitive model routing locked for this conversation.`,
          sensitive_model_lock: true
        });
      }
      if (lockedTierValue) {
        optimized.push({
          role: 'system',
          content: `${TIER_LOCK_MARKER}:${lockedTierValue}\nModel tier locked for this conversation.`,
          tier_lock: lockedTierValue
        });
      }
      optimized.push({
        role: 'system',
        content: `[System Note: 早期对话摘要（前情提要），请优先参考]\n${summaryText}`
      });

      // 自动预检索：用当前问题在已压缩区间的原文里查找相关片段
      const recallNote = buildRecallNote(store.history, currentPrompt);
      if (recallNote) {
        optimized.push({ role: 'system', content: recallNote });
      }

      // 保留总结节点之后的所有对话
      const remaining = ctx.slice(lastSummaryIndex + 1);
      return optimized.concat(remaining.map(formatMsg));
    }

    // --- 如果没有 summaryInfo，走默认的长度控制逻辑 ---
    const MAX_ROUNDS = 10;
    const KEEP_COUNT = MAX_ROUNDS * 2; // User + Assistant

    if (systemPrompt) optimized.push(systemPrompt);
    if (hasSensitiveModelLock) {
      optimized.push({
        role: 'system',
        content: `${SENSITIVE_MODEL_LOCK_MARKER}\nSensitive model routing locked for this conversation.`,
        sensitive_model_lock: true
      });
    }
    if (lockedTierValue) {
      optimized.push({
        role: 'system',
        content: `${TIER_LOCK_MARKER}:${lockedTierValue}\nModel tier locked for this conversation.`,
        tier_lock: lockedTierValue
      });
    }

    if (ctx.length <= KEEP_COUNT) {
      return optimized.concat(ctx.map(formatMsg));
    }

    // Keep Head and Tail
    const headCount = 2;
    const tailCount = KEEP_COUNT;

    if (ctx.length > headCount + tailCount) {
      const head = ctx.slice(0, headCount);
      const tail = ctx.slice(-tailCount);
      const skippedCount = ctx.length - headCount - tailCount;

      head.forEach(m => optimized.push(formatMsg(m)));
      
      optimized.push({
        role: 'system',
        content: `[System Note: ${skippedCount} messages from the middle of the conversation have been compressed/omitted to maintain context window. Please focus on the initial instructions and the most recent ${tailCount} messages.]`
      });

      tail.forEach(m => optimized.push(formatMsg(m)));
    } else {
       optimized = optimized.concat(ctx.map(formatMsg));
    }

    return optimized;
  };

  // Workspace tool continuations deliberately keep the original user message
  // in history: the backend replays tool calls/results without appending the
  // prompt a second time. Removing it here leaves the model with no task.
  const preserveWorkspacePrompt = Array.isArray(extraParams.workspace_tool_results)
    && extraParams.workspace_tool_results.length > 0;
  const optimizedHistory = optimizeContext(context, text, preserveWorkspacePrompt);
  // 已发生压缩时，告诉后端开放 session_memory_* 工具
  const compressedBoundary = getCompressedBoundary(store.history);
  const sessionMemoryInfo = compressedBoundary >= 0
    ? { enabled: true, compressed_until: compressedBoundary, total_messages: store.history.length }
    : null;

  // Combine text with fileContext for the current prompt
  let finalPrompt = text;
  let imageUrls = [];
  let hugeFiles = []; // Collect huge files to send separately
  const imageGenerationEnabled = store.settings ? store.settings.imageGenEnabled : false;

  if (attachments && attachments.length > 0) {
      attachments.forEach(att => {
          if (att.type === 'file') {
              if (att.content && att.content.length > 20000) {
                  hugeFiles.push({
                      name: att.name,
                      content: att.content
                  });
                  // Still add a reference in the prompt so the model knows it was provided
                  finalPrompt += `\n\n[Reference Document Content (${att.name})]:\n(Content is extremely large and will be processed separately)`;
              } else {
                  finalPrompt += `\n\n[Reference Document Content (${att.name})]:\n${att.content}`;
              }
              if (att.url) {
                  finalPrompt += `\n\n[Download ${att.name}]: ${att.url}`;
              }
          } else if (att.type === 'image') {
              imageUrls.push(att.url);
          }
      });
  }

  if (imageGenerationEnabled && imageUrls.length === 0 && looksLikeImageEditFollowUp(text)) {
    const inheritedImageUrl = findLatestConversationImageUrl(context);
    if (inheritedImageUrl) {
      imageUrls = [inheritedImageUrl];
    }
  }

  try {
    // 安全获取模型配置
    let activeModel = null;
    if (store.settings && store.settings.models && store.settings.models.length > 0) {
      activeModel = store.settings.models.find(m => m.id === store.settings.activeModelId) || store.settings.models[0];
    }
    
    // Get specialized models
    let textModel = null;
    let t2iModel = null;
    let i2iModel = null;
    
    if (store.settings && store.settings.models) {
      textModel = store.settings.models.find(m => m.id === store.settings.activeTextModelId) || activeModel;
      t2iModel = store.settings.models.find(m => m.id === store.settings.activeT2iModelId) || store.settings.models.find(m => m.type === 't2i');
      i2iModel = store.settings.models.find(m => m.id === store.settings.activeI2iModelId) || store.settings.models.find(m => m.type === 'i2i');
    }

    // Get deviceid and mode from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const deviceid = urlParams.get('deviceid') || '';
    const mode = urlParams.get('mode') || '';

    // 如果是 agent 模式且没有获取到配置，使用默认配置
    if (mode === 'agent' && !textModel) {
      textModel = {
        endpoint: 'https://api.rainsee.cn/v1/chat/completions',
        apiKey: 'sk-aabaaaaaaaa',
        model: 'rainsee-turbo',
        provider: 'openai'
      };
    }

    const dbAttachmentPool = await memoryDB.get('attachmentPool') || [];
    // 只携带当前会话的附件
    const currentSessionAttachments = dbAttachmentPool.filter(att => att.sessionId === store.currentSessionId);

    const payload = {
      prompt: finalPrompt,
      history: optimizedHistory, 
      image_urls: imageUrls,
      huge_files: hugeFiles, // Send huge files out-of-band to prevent prompt inflation
      attachment_pool: currentSessionAttachments, // 当前会话的附件池
      chat_id: chatId,
      thinking_mode: store.settings ? store.settings.thinkingEnabled : false,
      web_search: store.settings ? store.settings.webSearchEnabled : false,
      image_generation: imageGenerationEnabled,
      image_config: store.settings ? {
        aspect_ratio: store.settings.imageAspectRatio || '1:1',
        image_size: store.settings.imageSize || '1K'
      } : {},
      location_host: window.location.host,
      summary: store.pageContext ? store.pageContext.summary : '',
      content: store.pageContext ? store.pageContext.content : '',
      deviceid: deviceid,
      mode: mode,
      
      // Default model info (usually text), allow empty for agent mode
      endpoint: textModel ? textModel.endpoint : '',
      api_key: textModel ? textModel.apiKey : '',
      model: textModel ? textModel.model : '',
      
      // Specialized models
      text_model: textModel ? { provider: textModel.provider, endpoint: textModel.endpoint, api_key: textModel.apiKey, model: textModel.model } : null,
      t2i_model: t2iModel ? { id: t2iModel.id, provider: t2iModel.provider, endpoint: t2iModel.endpoint, task_endpoint: t2iModel.taskEndpoint || t2iModel.task_endpoint || '', image_input: t2iModel.imageInput || t2iModel.image_input || '', async: t2iModel.async === true, api_key: t2iModel.apiKey, model: t2iModel.model } : null,
      i2i_model: i2iModel ? { id: i2iModel.id, provider: i2iModel.provider, endpoint: i2iModel.endpoint, task_endpoint: i2iModel.taskEndpoint || i2iModel.task_endpoint || '', image_input: i2iModel.imageInput || i2iModel.image_input || '', async: i2iModel.async === true, api_key: i2iModel.apiKey, model: i2iModel.model } : null,
      
      token: store.token || '',
      user_id: store.userInfo ? (store.userInfo.id || store.userInfo.userId || store.userInfo.uid || '') : '',
      timestamp: Date.now(),
      ...(sessionMemoryInfo ? { session_memory: sessionMemoryInfo } : {}),
      ...extraParams // Merge extra params (like confirmed_command, disable_claude_tool)
    };

    // Route to the execution-capable server only when the request likely needs real server-side work.
    const isAgentRelated = shouldUseServerExecution(finalPrompt, extraParams);

    const targetEndpoint = textModel && textModel.endpoint ? textModel.endpoint : '';
    const response = await fetch(`${getChatApiUrl(targetEndpoint, isAgentRelated)}?action=chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: signal
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized: Invalid or missing token.');
      }
      throw new Error(`Network error: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('ReadableStream not supported in this browser.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const buildStreamError = (message, extra = {}) => {
      const err = new Error(message || 'Connection failed');
      err.name = extra.name || 'StreamError';
      err.isStreamError = true;
      Object.assign(err, extra);
      return err;
    };

    const parseSseFrame = (frame) => {
      const lines = frame.split(/\r?\n/);
      const dataLines = [];
      let eventName = '';

      lines.forEach((rawLine) => {
        if (!rawLine || rawLine.startsWith(':')) {
          return;
        }

        const separatorIndex = rawLine.indexOf(':');
        const field = separatorIndex === -1 ? rawLine : rawLine.slice(0, separatorIndex);
        let value = separatorIndex === -1 ? '' : rawLine.slice(separatorIndex + 1);
        if (value.startsWith(' ')) {
          value = value.slice(1);
        }

        if (field === 'event') {
          eventName = value;
        } else if (field === 'data') {
          dataLines.push(value);
        }
      });

      return {
        eventName,
        data: dataLines.join('\n').trim()
      };
    };

    const blockSensitiveContent = () => {
      streamFinished = true;
      doneNotified = true;
      typingQueue = [];
      contentFilter.discard();
      clearTypingTimer();
      cleanupStreamState();

      if (onSensitiveContent) {
        onSensitiveContent(SENSITIVE_CONTENT_BLOCK_MESSAGE);
      }
      reader.cancel('sensitive_content_detected').catch(() => {});
      if (extraParams.client_request_id) {
        cancelChatRequest(extraParams.client_request_id, text, extraParams);
      }
    };

    const filterAndEnqueueContent = (content) => {
      const normalizedContent = typeof content === 'string' ? content : String(content || '');
      const result = contentFilter.push(normalizedContent);
      if (result.blocked) {
        blockSensitiveContent();
        return true;
      }
      if (result.safeText) {
        enqueue('text', result.safeText);
      }
      return false;
    };

    const handleParsedPayload = (data, eventName = '') => {
      if (data.chat_id) {
        if (onChatId) onChatId(data.chat_id);
      }

      if (!data.type && eventName) {
        data.type = eventName;
      }

      if (data.type === 'meta') {
        if (onMeta) onMeta(data.content);
        return false;
      }

      if (data.type === 'account_blocked') {
        streamFinished = true;
        typingQueue = [];
        clearTypingTimer();
        const blockedPayload = data.content || {};
        try {
          const blockedAt = blockedPayload.blocked_at || new Date().toISOString();
          const blockedUntil = blockedPayload.blocked_until || new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
          localStorage.setItem('aihelp_account_blocked', JSON.stringify({
            ...blockedPayload,
            blocked_at: blockedAt,
            blocked_until: blockedUntil,
            duration_seconds: blockedPayload.duration_seconds || 7200
          }));
        } catch (e) {
          console.warn('Failed to persist account blocked state:', e);
        }
        window.dispatchEvent(new CustomEvent('aihelp-account-blocked', { detail: blockedPayload }));
        if (onAccountBlocked) onAccountBlocked(blockedPayload);
        notifyDone();
        return true;
      }

      if (data.type === 'think') {
        enqueue('think', data.content);
        return false;
      }

      if (data.type === 'text') {
        return filterAndEnqueueContent(data.content);
      }

      if (data.type === 'image_start') {
        if (onImageStart) onImageStart();
        return false;
      }

      if (data.type === 'image_task') {
        if (onImageTask) onImageTask(data.content || data);
        return false;
      }

      if (data.type === 'decision') {
        if (onDecision) onDecision(data.content);
        return false;
      }

      if (data.type === 'kb_judging') {
        if (onKnowledgeBaseJudging) onKnowledgeBaseJudging(data.content);
        return false;
      }

      const errorMessage =
        (data.type === 'error' && typeof data.content === 'string' && data.content.trim()) ||
        (typeof data.error === 'string' && data.error.trim()) ||
        (data.error && typeof data.error.message === 'string' && data.error.message.trim()) ||
        '';

      if (errorMessage && onError) {
        flushSensitiveFilters();
        streamFinished = true;
        typingQueue = [];
        clearTypingTimer();
        cleanupStreamState();
        onError(buildStreamError(errorMessage, { sseType: data.type || 'error' }));
        return true;
      }

      return false;
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() || '';

      for (const frame of frames) {
        const { data: dataStr, eventName } = parseSseFrame(frame);
        if (!dataStr) continue;

        if (dataStr === '[DONE]') {
          flushSensitiveFilters();
          streamFinished = true;
          if (!isTyping && typingQueue.length === 0 && onDone) {
            notifyDone();
          }
          return;
        }

        try {
          const data = JSON.parse(dataStr);
          const shouldStop = handleParsedPayload(data, eventName);
          if (shouldStop) {
            return;
          }
        } catch (e) {
          console.warn('Failed to parse SSE JSON:', dataStr);
        }
      }
    }

    if (buffer.trim()) {
      const { data: dataStr, eventName } = parseSseFrame(buffer);
      if (dataStr && dataStr !== '[DONE]') {
        try {
          const data = JSON.parse(dataStr);
          const shouldStop = handleParsedPayload(data, eventName);
          if (shouldStop) {
            return;
          }
        } catch (e) {
          console.warn('Failed to parse trailing SSE JSON:', dataStr);
        }
      }
    }
    
    // Fallback if [DONE] is not sent but stream closes
    flushSensitiveFilters();
    streamFinished = true;
    if (!isTyping && typingQueue.length === 0 && onDone) {
      notifyDone();
    }

  } catch (err) {
    streamFinished = true;
    typingQueue = []; // Clear queue on error
    clearTypingTimer();
    cleanupStreamState();
    if (onError && !isAborted) {
        if (err.name !== 'AbortError') {
            onError(err);
        }
    } else if (onError && err.name === 'AbortError') {
        // Still call onError for AbortError to match caller expectations
        onError(err);
    }
    if (err.name !== 'AbortError') {
        console.error('Stream Chat Error:', err);
    }
  }
}

export function getDisplayStreamErrorMessage(error, fallback = 'Connection failed') {
  if (!error || error.name === 'AbortError') {
    return '';
  }

  const message = typeof error.message === 'string' ? error.message.trim() : '';
  const resolvedMessage = message || fallback;
  if (!resolvedMessage) {
    return '';
  }

  if (error.isStreamError) {
    return resolvedMessage;
  }

  return resolvedMessage.startsWith('[') ? resolvedMessage : `[Error: ${resolvedMessage}]`;
}

export async function cancelChatRequest(requestId, text = '', extraParams = {}) {
  if (!requestId) {
    return;
  }

  let activeModel = null;
  if (store.settings && store.settings.models && store.settings.models.length > 0) {
    activeModel = store.settings.models.find(m => m.id === store.settings.activeModelId) || store.settings.models[0];
  }

  let textModel = null;
  if (store.settings && store.settings.models) {
    textModel = store.settings.models.find(m => m.id === store.settings.activeTextModelId) || activeModel;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode') || '';
  if (mode === 'agent' && !textModel) {
    textModel = {
      endpoint: 'https://api.rainsee.cn/v1/chat/completions',
      apiKey: 'sk-aabaaaaaaaa',
      model: 'rainsee-turbo',
      provider: 'openai'
    };
  }

  const targetEndpoint = textModel && textModel.endpoint ? textModel.endpoint : '';
  const isAgentRelated = shouldUseServerExecution(text, extraParams);

  try {
    await fetch(`${getChatApiUrl(targetEndpoint, isAgentRelated)}?action=cancel_request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ request_id: requestId }),
      keepalive: true
    });
  } catch (error) {
    console.warn('[cancelChatRequest] failed:', error);
  }
}

/**
 * Updates Soul, User, and Memory via Backend AI
 * 
 * @param {Object} data - { soul, user, memory, history }
 * @returns {Promise<Object>} - { soul, user, memory }
 */
export async function updateMemory(data) {
  const headers = {
    'Content-Type': 'application/json'
  };

  // Get active model config
  const activeModel = store.settings.models && store.settings.models.length > 0 
    ? (store.settings.models.find(m => m.id === store.settings.activeModelId) || store.settings.models[0])
    : { endpoint: '', apiKey: '', model: '' };

  const payload = {
    ...data,
    token: store.token,
    location_host: window.location.host,
    endpoint: activeModel.endpoint,
    api_key: activeModel.apiKey,
    model: activeModel.model
  };

  const payloadString = JSON.stringify(payload);
  const currentMd5 = CryptoJS.MD5(payloadString).toString();

  if (currentMd5 === lastPayloadMd5) {
    // console.log('[updateMemory] Payload unchanged, skipping update.');
    return null; 
  }

  try {
    const response = await fetch(`${API_URL}?action=update_memory`, {
      method: 'POST',
      headers,
      body: payloadString
    });

    if (!response.ok) {
      throw new Error(`Memory Update Failed: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.error) {
        throw new Error(result.error);
    }
    
    // 成功后保存 md5
    lastPayloadMd5 = currentMd5;
    
    return result;
  } catch (error) {
    console.error('Failed to update memory:', error);
    return null; // Fail silently or handle in UI
  }
}

/**
 * 异步静默压缩对话历史（不阻塞 UI）
 * 独立请求后端专属压缩接口 ContextCompressor.php，复用 config.php 中 applyFastConfig 定义的低价模型
 */
export async function summarizeHistoryAsync(messagesToCompress, previousSummary = '') {
  try {
    // 根据项目实际路径修改，假设与 API_URL 同域，或者通过统一网关
    const compressUrl = API_URL.replace('AiAssistantv5.php', 'ContextCompressor.php');
    
    const response = await fetch(compressUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: messagesToCompress,
        previous_summary: previousSummary,
        location_host: window.location.host
      })
    });

    if (!response.ok) {
      throw new Error(`Compression API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data && data.success && data.summary) {
      return data.summary;
    } else {
      throw new Error('Invalid summary format from backend');
    }
  } catch (error) {
    console.error('[Background Summary] failed:', error);
    return null; // 失败返回 null，下次继续尝试
  }
}

/**
 * 针对附件内容生成摘要 (Summary)
 */
export async function summarizeAttachmentAsync(fileName, content) {
  try {
    const summarizeUrl = API_URL.replace('AiAssistantv5.php', 'AttachmentSummarizer.php');
    
    // 去除特殊符号并限制字数
    let processedContent = content
      .replace(/[^\u4e00-\u9fa5a-zA-Z0-9\s,.，。！？!?]/g, '') // 仅保留中英文、数字、空格及基本标点
      .replace(/\s+/g, ' ') // 合并空格
      .trim()
      .slice(0, 10000); // 限制 10000 字

    const response = await fetch(summarizeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileName,
        content: processedContent,
        location_host: window.location.host
      })
    });

    if (!response.ok) {
      throw new Error(`Summarize API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data && data.success && data.summary) {
      return data.summary;
    } else {
      throw new Error('Invalid summary format from backend');
    }
  } catch (error) {
    console.error('[Attachment Summary] failed:', error);
    return `[Summary failed for ${fileName}]`;
  }
}

